import {
  AreaSeries,
  BarSeries,
  BaselineSeries,
  CandlestickSeries,
  LineSeries,
  LineType,
} from "lightweight-charts";
import { LINE_CHART_LINE_TYPE, PRICE_SERIES_STYLES } from "./seriesTransforms";

/**
 * Built-in price format for main OHLC series so tick steps match displayed precision.
 */
export function inferMainSeriesPriceFormat(data) {
  const fallback = { type: "price", precision: 5, minMove: 0.00001 };
  if (!Array.isArray(data) || data.length === 0) {
    return fallback;
  }
  const last = data[data.length - 1];
  const ref = Math.max(
    Math.abs(Number(last.close ?? last.value ?? 0)),
    Math.abs(Number(last.high ?? 0)),
    Math.abs(Number(last.low ?? 0)),
    Math.abs(Number(last.open ?? 0))
  );
  if (!Number.isFinite(ref) || ref === 0) {
    return fallback;
  }
  if (ref >= 10_000) {
    return { type: "price", precision: 2, minMove: 0.01 };
  }
  if (ref >= 1_000) {
    return { type: "price", precision: 3, minMove: 0.001 };
  }
  if (ref >= 100) {
    return { type: "price", precision: 4, minMove: 0.0001 };
  }
  return { type: "price", precision: 5, minMove: 0.00001 };
}

function lineTypeFromPayload(payload) {
  switch (payload?.lineChartLineType) {
    case LINE_CHART_LINE_TYPE.STEP:
      return LineType.WithSteps;
    case LINE_CHART_LINE_TYPE.CURVED:
      return LineType.Curved;
    default:
      return LineType.Simple;
  }
}

/**
 * Adds the main price series to a chart (TradingView-style types).
 */
export function addMainPriceSeries(chart, style, payload, colors) {
  const {
    bullishCandleBodyColor: up,
    bearishCandleBodyColor: down,
    bullishCandleBorderColor: bUp,
    bearishCandleBorderColor: bDn,
    bullishCandleWickColor: wUp,
    bearishCandleWickColor: wDn,
  } = colors;

  const lineT = lineTypeFromPayload(payload);
  const priceFormat = inferMainSeriesPriceFormat(payload?.data);

  switch (style) {
    case PRICE_SERIES_STYLES.HOLLOW:
      return chart.addSeries(CandlestickSeries, {
        upColor: "rgba(0,0,0,0)",
        downColor: down,
        borderUpColor: bUp,
        borderDownColor: bDn,
        wickUpColor: wUp,
        wickDownColor: wDn,
        priceFormat,
      });
    case PRICE_SERIES_STYLES.BARS:
      return chart.addSeries(BarSeries, {
        upColor: up,
        downColor: down,
        openVisible: true,
        thinBars: false,
        priceFormat,
      });
    case PRICE_SERIES_STYLES.LINE:
      return chart.addSeries(LineSeries, {
        color: up,
        lineWidth: 2,
        lineType: lineT,
        lastValueVisible: true,
        priceLineVisible: false,
        crosshairMarkerVisible: true,
        priceFormat,
      });
    case PRICE_SERIES_STYLES.KAGI:
      return chart.addSeries(LineSeries, {
        color: up,
        lineWidth: 2,
        lineType: LineType.WithSteps,
        lastValueVisible: true,
        priceLineVisible: false,
        crosshairMarkerVisible: true,
        priceFormat,
      });
    case PRICE_SERIES_STYLES.AREA:
      return chart.addSeries(AreaSeries, {
        lineColor: up,
        topColor: `${up}99`,
        bottomColor: `${down}00`,
        lineWidth: 2,
        lineType: lineT,
        lastValueVisible: true,
        priceLineVisible: false,
        priceFormat,
      });
    case PRICE_SERIES_STYLES.BASELINE: {
      const base =
        typeof payload?.baselinePrice === "number" &&
        Number.isFinite(payload.baselinePrice)
          ? payload.baselinePrice
          : 0;
      return chart.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: base },
        topLineColor: up,
        bottomLineColor: down,
        topFillColor1: `${up}55`,
        topFillColor2: `${up}10`,
        bottomFillColor1: `${down}55`,
        bottomFillColor2: `${down}10`,
        lineWidth: 2,
        lastValueVisible: true,
        priceLineVisible: false,
        priceFormat,
      });
    }
    case PRICE_SERIES_STYLES.HEIKIN_ASHI:
    case PRICE_SERIES_STYLES.RENKO:
    case PRICE_SERIES_STYLES.LINE_BREAK:
    case PRICE_SERIES_STYLES.RANGE:
    case PRICE_SERIES_STYLES.POINT_FIGURE:
    case PRICE_SERIES_STYLES.CANDLES:
    default:
      return chart.addSeries(CandlestickSeries, {
        upColor: up,
        downColor: down,
        borderUpColor: bUp,
        borderDownColor: bDn,
        wickUpColor: wUp,
        wickDownColor: wDn,
        priceFormat,
      });
  }
}

export function applyMainPriceSeriesColors(series, style, colors, payload) {
  if (!series) {
    return;
  }

  const {
    bullishCandleBodyColor: up,
    bearishCandleBodyColor: down,
    bullishCandleBorderColor: bUp,
    bearishCandleBorderColor: bDn,
    bullishCandleWickColor: wUp,
    bearishCandleWickColor: wDn,
  } = colors;

  const lineT = lineTypeFromPayload(payload);
  const priceFormat = inferMainSeriesPriceFormat(payload?.data);

  switch (style) {
    case PRICE_SERIES_STYLES.HOLLOW:
      series.applyOptions({
        upColor: "rgba(0,0,0,0)",
        downColor: down,
        borderUpColor: bUp,
        borderDownColor: bDn,
        wickUpColor: wUp,
        wickDownColor: wDn,
        priceFormat,
      });
      break;
    case PRICE_SERIES_STYLES.BARS:
      series.applyOptions({
        upColor: up,
        downColor: down,
        priceFormat,
      });
      break;
    case PRICE_SERIES_STYLES.LINE:
      series.applyOptions({ color: up, lineType: lineT, priceFormat });
      break;
    case PRICE_SERIES_STYLES.KAGI:
      series.applyOptions({
        color: up,
        lineType: LineType.WithSteps,
        priceFormat,
      });
      break;
    case PRICE_SERIES_STYLES.AREA:
      series.applyOptions({
        lineColor: up,
        topColor: `${up}99`,
        bottomColor: `${down}00`,
        lineType: lineT,
        priceFormat,
      });
      break;
    case PRICE_SERIES_STYLES.BASELINE:
      series.applyOptions({
        topLineColor: up,
        bottomLineColor: down,
        topFillColor1: `${up}55`,
        topFillColor2: `${up}10`,
        bottomFillColor1: `${down}55`,
        bottomFillColor2: `${down}10`,
        priceFormat,
      });
      break;
    case PRICE_SERIES_STYLES.HEIKIN_ASHI:
    case PRICE_SERIES_STYLES.RENKO:
    case PRICE_SERIES_STYLES.LINE_BREAK:
    case PRICE_SERIES_STYLES.RANGE:
    case PRICE_SERIES_STYLES.POINT_FIGURE:
    case PRICE_SERIES_STYLES.CANDLES:
    default:
      series.applyOptions({
        upColor: up,
        downColor: down,
        borderUpColor: bUp,
        borderDownColor: bDn,
        wickUpColor: wUp,
        wickDownColor: wDn,
        priceFormat,
      });
  }
}
